import React, { useState, useEffect } from 'react'

import styled from 'styled-components'
import Joi from 'joi'

import EducationalBackgroundListItem from './EducationalBackgroundListItem'
import EducationalBackgroundForm from './EducationalBackgroundForm'
import EmptyWrapper from '../../../../../../components/EmptyWrapper'

import axiosInstance from '../../../../../../services/config/AxiosIntance'

const EducationalBackground = ({
  onChange,
  educationalBackground,
  setEducationalBackground,
  educationalBackgrounds,
  setEducationalBackgrounds,
  fieldErrors,
  setFieldErrors,
}) => {
  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  const [educationalLevelList, setEducationalLevelList] = useState([])

  const defaultFields = () =>
    setEducationalBackground({
      id: Date.now(),
      educationLevel: educationalLevelList[0]?.label,
      school: '',
      schoolLocation: '',
      fieldOfStudy: '',
      from: new Date().toISOString().split('T')[0],
      to: new Date().toISOString().split('T')[0],
      currentStatus: 'Graduated',
    })

  const validateData = (payload) => {
    const educationalBackgroundSchema = Joi.object({
      id: Joi.alternatives()
        .try(Joi.number(), Joi.string())
        .label('Id')
        .required(),
      educationLevel: Joi.alternatives()
        .try(
          Joi.string().label('Education Level').required(),
          Joi.object({
            value: Joi.number().required(),
            label: Joi.string().required(),
          }).label('Education Level')
        )
        .required(),
      fieldOfStudy: Joi.string().label('Field of Study').required(),
      school: Joi.string().label('School').required(),
      schoolLocation: Joi.string().label('School Location').required(),
      currentStatus: Joi.string().label('Current Status').required(),
      from: Joi.date().label('Date From').required().messages({
        'date.base': '"Date From" must be a valid date',
        'any.required': '"Date From" is required',
      }),
      to: Joi.date()
        .label('Date To')
        .required()
        .custom((value, helpers) => {
          const from = helpers.state.ancestors[0].from
          if (!from) return value

          const fromDate = new Date(from)
          const toDate = new Date(value)

          const diffInDays = (toDate - fromDate) / (1000 * 60 * 60 * 24)

          if (diffInDays < 1) {
            return helpers.error('date.minDays')
          }

          return value
        })
        .messages({
          'date.base': '"Date To" must be a valid date',
          'any.required': '"Date To" is required',
          'date.minDays': '"Date To" must be at least 1 day after "Date From"',
        }),
    })

    const result = educationalBackgroundSchema.validate(payload, {
      abortEarly: false,
    })

    if (result.error) {
      const { error } = result
      const fieldNames = Object.keys(payload)
      const updatedErrors = fieldNames.reduce((acc, fieldName) => {
        acc[fieldName] =
          error?.details
            .find((detail) => detail.context.key === fieldName)
            ?.message.replace(/"/g, '') || ''
        return acc
      }, {})

      setFieldErrors((prev) => ({
        ...prev,
        educationalBackground: { ...updatedErrors },
      }))
      return false
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        educationalBackground: {},
      }))
      return true
    }
  }

  const handleEdit = (data) => {
    setShowForm(true)
    setEducationalBackground(data)
    setIsEdit(true)
  }

  const handleDelete = (id) => {
    setShowForm(false)
    const updatedBackgrounds = educationalBackgrounds.filter((e) => e.id !== id)
    setEducationalBackgrounds(updatedBackgrounds)
    setIsEdit(false)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setIsEdit(false)
  }

  const handleAddEducationalBackground = () => {
    if (!showForm) {
      setShowForm(true)
      defaultFields()
    } else {
      if (!validateData(educationalBackground)) return

      if (isEdit) {
        const selected = educationalBackgrounds.filter(
          (e) => e.id === educationalBackground?.id
        )[0]

        const updated = {
          ...educationalBackground,
          id: selected.id,
          educationLevel:
            educationalBackground?.educationLevel?.label ||
            educationalBackground?.educationLevel,
        }

        setEducationalBackgrounds(
          educationalBackgrounds.map((e) => (e.id === updated.id ? updated : e))
        )
        setIsEdit(false)
      } else {
        const newBackground = [
          ...educationalBackgrounds,
          {
            id: Date.now(),
            ...educationalBackground,
            educationLevel:
              educationalBackground.educationLevel?.label ||
              educationalLevelList[0]?.label,
            from: new Date(educationalBackground.from)
              .toISOString()
              .split('T')[0],
            to: new Date(educationalBackground.to).toISOString().split('T')[0],
          },
        ].sort((a, b) => new Date(a.to) - new Date(b.to)) // ascending by "to"

        setEducationalBackgrounds(newBackground)
      }

      defaultFields()
      setShowForm(false)
    }
  }

  const handleChange = (name, value) => {
    onChange({ [name]: value })
  }

  useEffect(() => {
    axiosInstance
      .get('/education-levels')
      .then((response) => {
        const data = response?.data?.data || []
        const formattedEducationalLevels = data.map((level) => ({
          value: level.id,
          label: level.name,
        }))
        setEducationalLevelList(formattedEducationalLevels)
      })
      .catch((error) => {
        console.error('Error fetching educational levels:', error)
      })
  }, [])

  return (
    <>
      <EducationalBackgroundListItemContainer>
        {educationalBackgrounds?.length > 0 ? (
          educationalBackgrounds.map((data, index) => (
            <EducationalBackgroundListItem
              key={index}
              id={data.id}
              educationLevel={data.educationLevel}
              school={data.school}
              schoolLocation={data.schoolLocation}
              fieldOfStudy={data.fieldOfStudy}
              from={data.from}
              to={data.to}
              currentStatus={data.currentStatus}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        ) : (
          <EmptyWrapper message="No educational background." />
        )}
      </EducationalBackgroundListItemContainer>
      <EducationalBackgroundForm
        educationalLevelList={educationalLevelList}
        educationalBackground={educationalBackground}
        onChange={handleChange}
        showForm={showForm}
        onAddEducationalBackground={handleAddEducationalBackground}
        onClose={handleCloseForm}
        isEdit={isEdit}
        fieldErrors={fieldErrors}
      />
    </>
  )
}

const EducationalBackgroundListItemContainer = styled.div`
  max-height: 400px;
  overflow: auto;
  margin-bottom: 1rem;
`

export default EducationalBackground
